import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardShell from "@/components/DashboardShell";
import { api } from "@/lib/api";
import { Eye, Users, MousePointerClick, TrendingUp, ExternalLink } from "lucide-react";

const Stat = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-border bg-card p-6">
    <div className="h-10 w-10 grid place-items-center rounded-xl bg-accent text-primary mb-4"><Icon size={18} /></div>
    <div className="font-display text-3xl font-semibold text-foreground">{value}</div>
    <div className="text-sm text-muted-foreground mt-0.5">{label}</div>
  </div>
);

export default function Analytics() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/timelines");
      setItems(data.filter((t) => t.status === "published"));
    })();
  }, []);

  const totalViews = items.reduce((s, t) => s + (t.views || 0), 0);

  return (
    <DashboardShell>
      <h1 className="font-display text-4xl font-semibold tracking-tight text-foreground">Analytics</h1>
      <p className="mt-2 text-muted-foreground">Understand how your published timelines are performing.</p>

      <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-5">
        <Stat icon={Eye} label="Total views" value={totalViews.toLocaleString()} />
        <Stat icon={TrendingUp} label="Published timelines" value={items.length} />
        <Stat icon={Users} label="Est. unique visitors" value={Math.round(totalViews * 0.72).toLocaleString()} />
        <Stat icon={MousePointerClick} label="Avg. events / timeline" value={items.length ? Math.round(items.reduce((s, t) => s + (t.events?.length || 0), 0) / items.length) : 0} />
      </div>

      <h2 className="mt-12 mb-4 font-display text-xl font-semibold text-foreground">Published timelines</h2>
      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border p-12 text-center text-muted-foreground">
          Publish a timeline to start collecting analytics.
          <div className="mt-4"><Link to="/create" className="text-primary font-medium hover:underline">Create one →</Link></div>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          {items.map((t, i) => (
            <div key={t.id} className={`flex items-center justify-between px-6 py-4 ${i > 0 ? "border-t border-border" : ""}`}>
              <div className="min-w-0">
                <div className="font-medium text-foreground truncate">{t.title}</div>
                <div className="text-xs text-muted-foreground">{t.events?.length || 0} events · {t.category}</div>
              </div>
              <div className="flex items-center gap-6 shrink-0">
                <div className="text-right"><div className="font-display font-semibold text-foreground">{(t.views || 0).toLocaleString()}</div><div className="text-xs text-muted-foreground">views</div></div>
                <a href={`/t/${t.creator_username}/${t.slug}`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary"><ExternalLink size={16} /></a>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardShell>
  );
}
